import { DateTime } from "luxon";

const now = DateTime.local().toLocaleString(DateTime.DATE_FULL); // e.g. "May 30, 2024"

export const variableGroups = {
  ACCOUNT: { icon: "mdi-account-outline" },
  ALLOWANCE: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["ALLOWANCE_CONSUMED", "PASS_ALLOWANCE_FULLY_USED"],
  },
  ASSIGNMENT: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_ASSIGNMENT", "ASSIGNMENT_ENDED"],
  },
  "CUSTOM PROPERTY": { icon: "mdi-pencil-outline" },
  BOOKING: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_BOOKING", "BOOKING_CANCELED", "BOOKING_ENDED"],
  },
  DOCUMENT: {
    icon: "mdi-file-document-outline",
    related_triggers: ["DOCUMENT_COMPLETE", "DOCUMENT_SENT"],
  },
  INVOICE: {
    icon: "mdi-receipt-outline",
    related_triggers: ["INVOICE_PAID", "INVOICE_DUE", "INVOICE_OVERDUE"],
  },
  ISSUE: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_ISSUE"],
  },
  MARKETPLACE: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_PRODUCT_SALE"],
  },
  ORGANIZATION: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_CHECKIN"],
  },
  PASS: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_PASS_SALE"],
  },
  PLAN: {
    icon: "mdi-calendar-blank-outline",
    related_triggers: ["NEW_PLAN", "PLAN_ENDED"],
  },
  "USER PROPERTY": { icon: "mdi-account-circle-outline" },
  TOUR: { icon: "mdi-calendar-range-outline", related_triggers: ["NEW_TOUR"] },
  OTHER: { icon: "mdi-calendar-blank-outline" },
  "CUSTOM MACROS": { icon: "mdi-code-tags" },
};

// Triggers a "delay until an action is complete" step can wait on, keyed by the context
// step's object type (matches the backend getObjectType()). DOCUMENT_STATUS_CHANGED is added later (SDWEB-18472).
export const triggersByObjectType = {
  ACCOUNT: [
    { value: "ACCOUNT_TYPE_CHANGED", label: "Account type changed" },
    { value: "TOUR_STAGE_CHANGED", label: "Tour stage changed" },
  ],
  DOCUMENT: [{ value: "DOCUMENT_COMPLETE", label: "Document complete" }],
};

// The object type a delay trigger watches, or null when it is not a delay trigger at all
export const delayTriggerObjectType = (trigger) =>
  Object.keys(triggersByObjectType).find((object_type) =>
    triggersByObjectType[object_type].some((t) => t.value === trigger),
  ) || null;

// ACCOUNT triggers watch the enrolled account itself, so they need no context step.
// Every other object type is produced by an earlier step, which the delay has to point at
export const delayTriggerNeedsContextStep = (trigger) => {
  const object_type = delayTriggerObjectType(trigger);

  return object_type !== null && object_type !== "ACCOUNT";
};

// Custom property types
export const inputTypes = {
  TEXTAREA: {
    icon: "mdi-text-box-outline",
    hideFromConditions: true,
  },
  TEXT: {
    icon: "mdi-text-box-outline",
  },
  EMAIL: {
    icon: "mdi-email-outline",
    eg: "john@email.com",
  },
  PHONE: {
    icon: "mdi-cellphone",
    eg: "123 456 7890",
  },
  NUMBER: {
    icon: "mdi-numeric",
    eg: 1,
  },
  SELECT: {
    icon: "mdi-button-pointer",
  },
  MULTIPLE: {
    icon: "mdi-button-pointer",
    hideFromEditor: true,
  },
};

/**
 * valueInputType
 * - CURRENCY
 * - DATE
 * - DAY_OF_WEEK
 * - DURATION
 * - NUMBER
 * - SELECT_ID
 * - SELECT_TEXT_FIXED
 * - TEXT
 * - TEXT_PHONE
 * - TIME
 * - WALLET
 */
export const variablesMeta = {
  SHORTCUT: {
    label: "Account has...",
    valueInputType: "SELECT_TEXT_FIXED",
    group: "ACCOUNT",
    // icon: "mdi-calendar-blank-outline",
  },

  TOUR_DATE: {
    label: "Tour booking date/time",
    valueInputType: "DATE",
    group: "TOUR",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    dateModifierUnitsOptions: [
      { text: "Minutes", value: "MINUTE" },
      { text: "Hours", value: "HOUR" },
      { text: "Days", value: "DAY" },
      { text: "Weeks", value: "WEEK" },
      { text: "Months", value: "MONTH" },
    ],
  },
  ACCOUNT_EMAIL: {
    label: "Account's email",
    valueInputType: "TEXT",
    icon: "mdi-email-outline",
    group: "ACCOUNT",
    hideFromConditions: true,
  },
  ACCOUNT_PRIMARY_LOCATION_ID: {
    label: "Account's primary location",
    valueInputType: "SELECT_ID",
    eg: "Location 1",
    icon: "mdi-map-marker-outline",
    group: "ACCOUNT",
  },
  ACCOUNT_STATUS: {
    label: "Account status",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
  },
  ACCOUNT_CHECKIN_STATUS: {
    label: "Check-in status",
    valueInputType: "SELECT_TEXT_FIXED",
    group: "ACCOUNT",
    eg: "Checked in",
  },
  ACCOUNT_SOURCE: {
    label: "User source",
    valueInputType: "SELECT_ID",
    group: "USER PROPERTY",
  },
  ACCOUNT_TEAM_MEMBER_STATUS: {
    label: "Team member status",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
  },
  ACCOUNT_PLAN_END_DATE: {
    label: "Plan end date",
    valueInputType: "DATE",
    group: "PLAN",
    eg: now,
    icon: "mdi-repeat",
  },
  ACCOUNT_PLAN_NAME: {
    label: "Plan name",
    valueInputType: "TEXT",
    group: "PLAN",
    eg: "Full Time Member",
    hint: "The name of the plan that has been added to the account",
    icon: "mdi-repeat",
  },
  ACCOUNT_PLAN_PRICE: {
    label: "Plan price",
    valueInputType: "CURRENCY",
    group: "PLAN",
    eg: "500",
    hint: "The recurring cost of the plan",
    icon: "mdi-repeat",
  },
  ACCOUNT_PLAN_START_DATE: {
    label: "Plan start date",
    valueInputType: "DATE",
    group: "PLAN",
    eg: now,
    icon: "mdi-repeat",
  },
  ACCOUNT_PLAN_TEMPLATE_ID: {
    label: "Plan template",
    valueInputType: "SELECT_ID",
    eg: "Full Time Member",
    icon: "mdi-repeat",
    group: "PLAN",
  },
  ACCOUNT_PLAN_TYPE: {
    label: "Is team/personal plan",
    valueInputType: "SELECT_TEXT_FIXED",
    group: "PLAN",
    eg: "Team",
    hint: "Indicates if the plan belongs to a team or is a personal plan",
    icon: "mdi-account-group",
  },
  ACCOUNT_ACTIVE_PLAN_TEMPLATE_ID: {
    label: "Active plan",
    valueInputType: "SELECT_ID",
    hint: "In-trial or active plans of the account",
    eg: "John's Full Time Member plan",
    icon: "mdi-repeat",
    group: "ACCOUNT",
    hideFromEditor: true,
  },
  ACCOUNT_ACTIVE_PLAN_ID: {
    hideFromConditions: true,
    hideFromEditor: true,
  },
  ACCOUNT_ACTIVE_ASSIGNMENT_ID: {
    hideFromConditions: true,
    hideFromEditor: true,
  },
  ACCOUNT_ACTIVE_PASS_PRODUCT_ID: {
    label: "Active pass",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
    hint: "Accounts that have bought this pass and still have remaining allowance, including those with access to allowance through a team pass purchase",
    eg: "10 Day Pass",
    icon: "mdi-wallet-bifold",
    hideFromEditor: true,
  },
  // Offered only through the "Account has..." shortcuts
  ACCOUNT_INQUIRY_FORM_STATUS: {
    hideFromConditions: true,
    hideFromEditor: true,
  },
  ACCOUNT_INQUIRY_PLAN_TEMPLATE_ID: {
    label: "Requested plan",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
    icon: "mdi-repeat",
    eg: "Full Time Member",
  },
  ACCOUNT_INQUIRY_PRODUCT_ID: {
    label: "Requested pass",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
    icon: "mdi-wallet-bifold",
    eg: "10 Day Pass",
  },
  PASS_SALE_PRODUCT_ID: {
    label: "Pass",
    valueInputType: "SELECT_ID",
    group: "PASS",
    icon: "mdi-wallet-bifold",
    eg: "10 Day Pass",
  },
  WALLET_ACCESS_TEMPLATE_ID: {
    label: "Allowance for",
    valueInputType: "SELECT_ID",
    hint: "E.g. Bookings of any Meeting Room",
    group: "ALLOWANCE",
    icon: "mdi-wallet-bifold-outline",
    eg: "Bookings of any Meeting Rooom",
  },
  ACCOUNT_WALLET_ALLOWANCE_AMOUNT: {
    label: "Current allowance balance",
    valueInputType: "WALLET",
    hint: "E.g. 2 hours",
    group: "ACCOUNT",
    eg: 1,
    icon: "mdi-numeric",
    hideFromEditor: true,
  },
  PRODUCT_SALE_PRODUCT_TAG: {
    label: "Product tag",
    valueInputType: "SELECT_ID",
    hint: "E.g. Food",
    group: "MARKETPLACE",
    eg: "Coffee",
    icon: "mdi-tag-outline",
    hideFromEditor: true,
  },
  PRODUCT_SALE_PRODUCT_ID: {
    label: "Product",
    valueInputType: "SELECT_ID",
    group: "MARKETPLACE",
    eg: "Espresso",
    icon: "mdi-tag-outline",
  },
  BOOKED_TIME: {
    label: "Booked time",
    valueInputType: "TIME",
    group: "BOOKING",
    eg: "9:00am",
    hint: "The time of each interval during a booking",
    icon: "mdi-calendar-clock",
  },
  BOOKING_DATE: {
    label: "Booking date/time",
    valueInputType: "DATE",
    group: "BOOKING",
    eg: now,
    hint: "The date the booking will take place",
    icon: "mdi-calendar-edit-outline",
    dateModifierUnitsOptions: [
      { text: "Minutes", value: "MINUTE" },
      { text: "Hours", value: "HOUR" },
      { text: "Days", value: "DAY" },
      { text: "Weeks", value: "WEEK" },
      { text: "Months", value: "MONTH" },
    ],
  },
  BOOKING_DAY_OF_WEEK: {
    label: "Day of the week of booking",
    valueInputType: "DAY_OF_WEEK",
    group: "BOOKING",
    eg: "Saturday",
    hint: "The day of the week when a booking starts (not the day it was created)",
    icon: "mdi-calendar-edit-outline",
  },
  BOOKING_DURATION: {
    label: "Booking duration",
    valueInputType: "DURATION",
    group: "BOOKING",
    eg: "1 hour",
    hint: "The length of time the booking lasts",
    icon: "mdi-calendar-clock",
  },
  BOOKING_PRICE: {
    label: "Booking price",
    valueInputType: "CURRENCY",
    group: "BOOKING",
    eg: "20",
    hint: "The total cost of the booking",
    icon: "mdi-calendar-edit-outline",
  },
  BOOKING_RESOURCE_ID: {
    label: "Resource",
    valueInputType: "SELECT_ID",
    group: "BOOKING",
    eg: "Meeting Room 1",
    icon: "mdi-shape-outline",
  },
  BOOKING_RESOURCE_TYPE_ID: {
    label: "Resource type",
    valueInputType: "SELECT_ID",
    group: "BOOKING",
    eg: "Meeting",
    icon: "mdi-shape-outline",
  },
  BOOKING_SOURCE: {
    label: "Booking source",
    valueInputType: "SELECT_TEXT_FIXED",
    group: "BOOKING",
    eg: "Drop-in",
    icon: "mdi-calendar-edit-outline",
  },
  BOOKING_START_TIME: {
    label: "Booking start time",
    valueInputType: "TIME",
    group: "BOOKING",
    eg: "9:00am",
    hint: "The time that a booking starts",
    icon: "mdi-calendar-clock",
  },
  // The current date at the moment of execution. Used internally as the
  // reference point for the "is less/more than" dynamic date operators
  CURRENT_DATE: {
    label: "Current date",
    valueInputType: "DATE",
    group: "OTHER",
    icon: "mdi-calendar-blank-outline",
    hideFromConditions: true,
    hideFromEditor: true,
  },
  DOCUMENT_NAME: {
    label: "Document name",
    valueInputType: "TEXT",
    group: "DOCUMENT",
    eg: "Office lease agreement",
    icon: "mdi-file-document-outline",
  },
  DOCUMENT_STATUS: {
    label: "Document status",
    valueInputType: "SELECT_TEXT_FIXED",
    group: "DOCUMENT",
    icon: "mdi-file-document-outline",
  },
  INVOICE_BALANCE: {
    label: "Invoice balance",
    valueInputType: "CURRENCY",
    group: "INVOICE",
    eg: "200",
    hint: 'The total of the invoice line items, subtracting the payments made. A.k.a "Amount due"',
    icon: "mdi-invoice-text-multiple-outline",
  },
  INVOICE_DUE_DATE: {
    label: "Invoice due date",
    valueInputType: "DATE",
    group: "INVOICE",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  INVOICE_NUMBER: {
    label: "Invoice number",
    group: "INVOICE",
    eg: "12345",
    hint: "The unique identifying number for an invoice",
    icon: "mdi-invoice-text-multiple-outline",
    hideFromConditions: true,
  },
  INVOICE_PAID_DATE: {
    label: "Invoice paid date",
    valueInputType: "DATE",
    group: "INVOICE",
    eg: now,
    hint: "The date the invoice balance became zero",
    icon: "mdi-calendar-blank-outline",
  },
  INVOICE_STATUS: {
    label: "Invoice status",
    valueInputType: "SELECT_ID",
    group: "INVOICE",
    icon: "mdi-invoice-clock-outline",
  },
  INVOICE_TOTAL: {
    label: "Invoice total",
    valueInputType: "CURRENCY",
    group: "INVOICE",
    eg: "200",
    hint: "The total of the invoice line items excluding any payments made, including tax",
    icon: "mdi-invoice-text-multiple-outline",
  },
  ISSUE_CATEGORY: {
    label: "Issue type",
    valueInputType: "SELECT_TEXT_FIXED",
    hint: "E.g. Wi-Fi",
    eg: "Wi-Fi",
    icon: "mdi-flag-outline",
    group: "ISSUE",
  },
  LOCATION_ID: {
    label: "Location",
    valueInputType: "SELECT_ID",
    eg: "Location 1",
    icon: "mdi-map-marker-outline",
    group: "ORGANIZATION",
  },
  NTH_TIME: {
    label: "Number of occurrences",
    valueInputType: "NUMBER",
    hint: "The total number of times this account has performed this action (including the current instance)",
    eg: 1,
    icon: "mdi-counter",
    group: "OTHER",
  },
  ACCOUNT_NEXT_PLAN_START_DATE: {
    label: "Next Plan start date",
    valueInputType: "DATE",
    hint: "The next plan that will start for an account",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_NEXT_PLAN_END_DATE: {
    label: "Next Plan end date",
    valueInputType: "DATE",
    hint: "The next plan that will end for an account",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_FINAL_PLAN_END_DATE: {
    label: "Final Plan end date",
    valueInputType: "DATE",
    hint: "The date when a plan ends, and the account will have no other in-trial, active, or upcoming plans",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_NEXT_ASSIGNMENT_END_DATE: {
    label: "Next assignment end date",
    valueInputType: "DATE",
    hint: "The next assignment that will end for an account",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_NEXT_ASSIGNMENT_START_DATE: {
    label: "Next assignment start date",
    valueInputType: "DATE",
    hint: "The next assignment that will start for an account",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_MIN_DUE_INVOICE_DUE_DATE: {
    label: "Date of the oldest due/overdue invoice",
    valueInputType: "DATE",
    hint: "An invoice with the oldest due date, which is due or overdue",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_LAST_BOOKING_DATE: {
    label: "Most recent booking",
    valueInputType: "DATE",
    hint: "An account's most recent booking. Future bookings are not included",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    excludedDateModifierOptions: ["BEFORE"],
    dateModifierOptionTooltips: {
      BEFORE:
        "The most recent booking will always be in the past. Try using the 'After' or 'On' options instead",
    },
  },
  ACCOUNT_LAST_MARKETPLACE_PURCHASE_DATE: {
    label: "Last marketplace purchase date",
    valueInputType: "DATE",
    hint: "The date of the last marketplace purchase made by the account",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    excludedDateModifierOptions: ["BEFORE"],
    dateModifierOptionTooltips: {
      BEFORE:
        "The last marketplace purchase will always be in the past. Try using the 'After' or 'On' options instead",
    },
  },
  ACCOUNT_LAST_MESSAGE_SENT_DATE: {
    label: "Last message sent date",
    valueInputType: "DATE",
    hint: "The date when the last message was sent by the account",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    excludedDateModifierOptions: ["BEFORE"],
    dateModifierOptionTooltips: {
      BEFORE:
        "The last message sent will always be in the past. Try using the 'After' or 'On' options instead",
    },
  },
  ACCOUNT_LAST_CHECKIN_DATE: {
    label: "Most recent check-in",
    valueInputType: "DATE",
    hint: "An account's most recent check-in",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    excludedDateModifierOptions: ["BEFORE"],
    dateModifierOptionTooltips: {
      BEFORE:
        "The most recent check-in will always be in the past. Try using the 'After' or 'On' options instead",
    },
  },
  ACCOUNT_LAST_MOBILE_APP_USE_DATE: {
    label: "Last mobile app use date",
    valueInputType: "DATE",
    hint: "The date when the user last used the mobile app",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    excludedDateModifierOptions: ["BEFORE"],
    dateModifierOptionTooltips: {
      BEFORE:
        "The last app use will always be in the past. Try using the 'After' or 'On' options instead",
    },
  },
  ACCOUNT_NEXT_BOOKING_DATE: {
    label: "Next booking date",
    valueInputType: "DATE",
    hint: "An account's closest upcoming booking",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_PENDING_INVOICE_DUE_DATE: {
    label: "Pending invoice due date",
    valueInputType: "DATE",
    hint: "An account's pending invoice due date",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    excludedDateModifierOptions: ["ON", "AFTER"],
    dateModifierOptionTooltips: {
      ON: "Pending invoices change status to 'due' on this date. Try using the 'Invoice became due' trigger instead",
      AFTER:
        "Pending invoices change status to 'due' on this date. Try using the 'Invoice became due' trigger plus a delay step instead",
    },
    dateModifierUnitsOptions: [{ text: "Days", value: "DAY" }],
    maxValue: 27,
  },
  ACCOUNT_NEXT_TOUR_DATE: {
    label: "Upcoming tour booking date",
    valueInputType: "DATE",
    hint: "An account's closest upcoming tour booking",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_LAST_TOUR_DATE: {
    label: "Last tour booking date",
    valueInputType: "DATE",
    hint: "An account's most recent completed tour booking",
    group: "ACCOUNT",
    eg: now,
    icon: "mdi-calendar-blank-outline",
  },
  ACCOUNT_SIGNUP_DATE: {
    label: "Account created date",
    valueInputType: "DATE",
    group: "ACCOUNT",
    excludedDateModifierOptions: ["BEFORE"],
    dateModifierOptionTooltips: {
      BEFORE:
        "The account created date will always be in the past. Try using the 'After' or 'On' options instead",
    },
  },
  TRIGGER_DATE: {
    label: "Date the Automation was triggered",
    valueInputType: "DATE",
    hint: "The date that an account is enrolled on to an Automation",
    group: "OTHER",
    eg: now,
    icon: "mdi-calendar-blank-outline",
    hideFromConditions: true,
  },
  TRIGGER_DAY_OF_WEEK: {
    label: "Automation trigger date of week",
    valueInputType: "DAY_OF_WEEK",
    group: "OTHER",
  },
  ACCOUNT_TOTAL_CONFIRMED_BOOKINGS: {
    label: "Number of bookings made",
    valueInputType: "NUMBER",
    group: "ACCOUNT",
    eg: "5",
    hint: "Cancelled bookings are ignored",
    icon: "mdi-invoice-text-multiple-outline",
  },
  ACCOUNT_TOTAL_INVOICES_BALANCE: {
    label: "Total account invoice(s) balance",
    valueInputType: "CURRENCY",
    group: "ACCOUNT",
    eg: "950",
    hint: "The total of all unpaid (upcoming/due/overdue) invoice balances",
    icon: "mdi-invoice-text-multiple-outline",
  },
  ACCOUNT_TOUR_STAGE_LABEL_ID: {
    label: "Tour stage",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
    eg: "Booked",
    icon: "mdi-map-marker-path",
  },
  ACCOUNT_TYPE_LABEL_ID: {
    label: "Account type",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
    eg: "Lead",
    icon: "mdi-folder-account-outline",
  },
  ACCOUNT_REFERRAL_CODE: {
    label: "Account referral code",
    valueInputType: "TEXT",
    group: "ACCOUNT",
    eg: "BOB-X5H7",
    hint: "The unique referral code belonging to this account",
    icon: "mdi-account-arrow-right-outline",
  },
  ACCOUNT_REFERRER_ACCOUNT_ID: {
    hideFromConditions: true,
    hideFromEditor: true,
  },
  ASSIGNMENT_PRICE: {
    label: "Assignment price",
    valueInputType: "CURRENCY",
    group: "ASSIGNMENT",
    eg: "950",
    hint: "The recurring or one-time cost of the assignment",
    icon: "mdi-calendar-edit-outline",
  },
  ASSIGNMENT_RESOURCE_ID: {
    label: "Resource",
    valueInputType: "SELECT_ID",
    group: "ASSIGNMENT",
    eg: "Meeting Room 1",
    icon: "mdi-shape-outline",
  },
  ASSIGNMENT_RESOURCE_TYPE_ID: {
    label: "Resource type",
    valueInputType: "SELECT_ID",
    group: "ASSIGNMENT",
    eg: "Office",
    icon: "mdi-shape-outline",
  },
  ASSIGNMENT_START_DATE: {
    label: "Assignment start date",
    valueInputType: "DATE",
    group: "ASSIGNMENT",
    eg: now,
    hint: "The date the assignment starts",
    icon: "mdi-calendar-edit-outline",
  },
  ASSIGNMENT_END_DATE: {
    label: "Assignment end date",
    valueInputType: "DATE",
    group: "ASSIGNMENT",
    eg: now,
    hint: "The date the assignment ends, empty if the assignment has no end date",
    icon: "mdi-calendar-edit-outline",
  },
  ORGANIZATION_NAME: {
    label: "Organization name",
    group: "ORGANIZATION",
    eg: "ACME",
    icon: "mdi-briefcase-account-outline",
    hideFromConditions: true,
  },
};

export const emailVariablesMeta = {
  MACRO_ASSIGNMENT_DETAILS: {
    label: "Assignment Details",
    group: "ASSIGNMENT",
    eg: "Resource, date, etc.",
    icon: "mdi-information-box-outline",
    isMacro: true,
  },
  MACRO_BOOKING_DETAILS: {
    label: "Booking Details",
    group: "BOOKING",
    eg: "Resource, date, etc.",
    icon: "mdi-information-box-outline",
    isMacro: true,
  },
  MACRO_GET_STARTED_BUTTON: {
    label: "Download the app button",
    group: "ACCOUNT",
    icon: "mdi-button-pointer",
    isMacro: true,
  },
  MACRO_INVOICE_DETAILS: {
    label: "Invoice details",
    group: "INVOICE",
    eg: "Invoice #, date, etc.",
    icon: "mdi-information-box-outline",
    isMacro: true,
  },
  MACRO_INVOICE_PDF_BUTTON: {
    label: "Invoice PDF button",
    group: "INVOICE",
    icon: "mdi-file-pdf-box",
    isMacro: true,
  },
  MACRO_NEW_PLAN_DETAILS: {
    label: "Plan details",
    group: "PLAN",
    eg: "Plan start date, price, etc.",
    icon: "mdi-information-box-outline",
    isMacro: true,
  },
  MACRO_VIEW_BOOKINGS_BUTTON: {
    label: "View bookings button",
    group: "BOOKING",
    icon: "mdi-button-pointer",
    isMacro: true,
  },
  MACRO_VIEW_INVOICE_BUTTON: {
    label: "View invoice button",
    group: "INVOICE",
    icon: "mdi-button-pointer",
    isMacro: true,
  },
  MACRO_VIEW_PLAN_BUTTON: {
    label: "View plan button",
    group: "PLAN",
    icon: "mdi-button-pointer",
    isMacro: true,
  },
};

export const defaultProperties = {
  ACCOUNT_FIRST_NAME: {
    label: "First name",
    valueInputType: "TEXT",
    group: "ACCOUNT",
    eg: "John",
    icon: "mdi-card-account-details-outline",
  },
  ACCOUNT_ID: {
    label: "Full name",
    valueInputType: "SELECT_ID",
    group: "ACCOUNT",
    eg: "John Smith",
    icon: "mdi-account-outline",
  },
  ACCOUNT_POSITION: {
    label: "Position",
    valueInputType: "TEXT",
    group: "USER PROPERTY",
    eg: "CEO",
    icon: "mdi-account-details-outline",
  },
  ACCOUNT_SHORT_BIO: {
    label: "Short bio",
    valueInputType: "TEXT",
    group: "USER PROPERTY",
    hideFromConditions: true,
    hideFromEditor: true,
  },
  ACCOUNT_SKILLS: {
    label: "Skills",
    valueInputType: "TEXT",
    group: "USER PROPERTY",
    hideFromConditions: true,
    hideFromEditor: true,
  },
  ACCOUNT_COMPANY: {
    label: "Company",
    valueInputType: "TEXT",
    group: "USER PROPERTY",
    eg: "ACME",
    icon: "mdi-briefcase-account-outline",
  },
  ACCOUNT_INTEREST: {
    label: "Interest",
    valueInputType: "TEXT",
    group: "USER PROPERTY",
    icon: "mdi-star-outline",
    hideFromEditor: true,
  },
  ACCOUNT_PHONE: {
    label: "Phone number",
    valueInputType: "TEXT_PHONE",
    group: "ACCOUNT",
    icon: "mdi-phone-outline",
    hideFromConditions: true,
  },
  ACCOUNT_CITY: {
    label: "City",
    valueInputType: "TEXT",
    group: "USER PROPERTY",
    eg: "Vancouver",
    icon: "mdi-city-variant-outline",
  },
};

export const shortcutConditions = [
  {
    text: "Used the app",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [
          { variable: "ACCOUNT_LAST_MOBILE_APP_USE_DATE" },
        ],
      },
    },
  },
  {
    text: "Never used the app",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [
          { variable: "ACCOUNT_LAST_MOBILE_APP_USE_DATE" },
        ],
      },
    },
  },

  {
    text: "Completed an inquiry form",
    value: {
      shortcut_condition: {
        condition_operation: "EQUALS",
        condition_parameters: [
          { variable: "ACCOUNT_INQUIRY_FORM_STATUS" },
          { value: "Submitted" },
        ],
      },
    },
  },
  {
    text: "Not completed an inquiry form",
    value: {
      shortcut_condition: {
        condition_operation: "EQUALS",
        condition_parameters: [
          { variable: "ACCOUNT_INQUIRY_FORM_STATUS" },
          { value: "Not submitted" },
        ],
      },
    },
  },

  {
    text: "Upcoming tour",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_NEXT_TOUR_DATE" }],
      },
    },
  },
  {
    text: "No upcoming tour",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_NEXT_TOUR_DATE" }],
      },
    },
  },
  {
    text: "Completed tour",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_LAST_TOUR_DATE" }],
      },
    },
  },
  {
    text: "Never completed a tour",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_LAST_TOUR_DATE" }],
      },
    },
  },

  {
    text: "Upcoming booking(s)",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_NEXT_BOOKING_DATE" }],
      },
    },
  },
  {
    text: "No upcoming booking(s)",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_NEXT_BOOKING_DATE" }],
      },
    },
  },

  {
    text: "Ever made a booking",
    value: {
      shortcut_condition: {
        condition_operation: "GT",
        condition_parameters: [
          { variable: "ACCOUNT_TOTAL_CONFIRMED_BOOKINGS" },
          { value: "0" },
        ],
      },
    },
    hint: "Excludes bookings that were cancelled",
  },
  {
    text: "Never made a booking",
    value: {
      shortcut_condition: {
        condition_operation: "EQUALS",
        condition_parameters: [
          { variable: "ACCOUNT_TOTAL_CONFIRMED_BOOKINGS" },
          { value: "0" },
        ],
      },
    },
  },

  {
    text: "Active plan(s)",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_ACTIVE_PLAN_ID" }],
      },
    },
    hint: "In-trial and active plans are included, while ended and upcoming plans are not. This also applies to plans purchased by the team",
  },
  {
    text: "No active plan(s)",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_ACTIVE_PLAN_ID" }],
      },
    },
    hint: "In-trial and active plans are included, while ended and upcoming plans are not. This also applies to plans purchased by the team",
  },

  {
    text: "Upcoming plan",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_NEXT_PLAN_START_DATE" }],
      },
    },
  },
  {
    text: "No upcoming plan",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_NEXT_PLAN_START_DATE" }],
      },
    },
  },

  {
    text: "Upcoming assignment",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [
          { variable: "ACCOUNT_NEXT_ASSIGNMENT_START_DATE" },
        ],
      },
    },
  },
  {
    text: "No upcoming assignment",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [
          { variable: "ACCOUNT_NEXT_ASSIGNMENT_START_DATE" },
        ],
      },
    },
  },

  {
    text: "Active assignment(s)",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_ACTIVE_ASSIGNMENT_ID" }],
      },
    },
  },
  {
    text: "No active assignment(s)",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_ACTIVE_ASSIGNMENT_ID" }],
      },
    },
  },

  {
    text: "Active pass(es)",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_ACTIVE_PASS_PRODUCT_ID" }],
      },
    },
    hint: "A pass is considered active if it has not expired and still has a remaining allowance or an overage discount. This includes passes purchased by the team",
  },
  {
    text: "No active pass(es)",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_ACTIVE_PASS_PRODUCT_ID" }],
      },
    },
    hint: "A pass is considered active if it has not expired and still has a remaining allowance or an overage discount. This includes passes purchased by the team",
  },

  {
    text: "Used valid referral code",
    value: {
      shortcut_condition: {
        condition_operation: "NOT_EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_REFERRER_ACCOUNT_ID" }],
      },
    },
    hint: "The referral code used at signup points to a real member in this organization",
  },
  {
    text: "Not used a valid referral code",
    value: {
      shortcut_condition: {
        condition_operation: "EMPTY",
        condition_parameters: [{ variable: "ACCOUNT_REFERRER_ACCOUNT_ID" }],
      },
    },
    hint: "No referral code was used at signup, or the code does not point to a real member in this organization",
  },
];
